import { type NavigationTypeType, strings } from "@quickpoint.me/shared";
import { type ReactNode, useContext, useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import { NavigationType } from "../../state/navigation";
import { PageSizeWithPaddingSelector } from "../../state/page";
import { IsPreviewGenerator } from "../../state/ui";
import { PositionContext } from "./DisplayElements";

export const StyledSafariWrapper = styled.section<{
	$width: number;
	$height: number;
	$navigationType: NavigationTypeType;
	$hasWidescreen?: boolean;
	$isInLandscape: boolean;
	$forceSingleColumn: boolean;
}>`
  display: grid;
  align-items: ${(props) => (props.$forceSingleColumn ? "start" : "center")};
  justify-items: center;
  position: ${(props) => (props.$navigationType === "scroll" ? "relative" : "absolute")};
  height: ${(props) =>
		props.$navigationType === "scroll" && !props.$isInLandscape
			? "auto"
			: `${props.$height}px`};
  width: ${(props) => (props.$hasWidescreen ? "100%" : `${props.$width}px`)};
  /* receives programmatic focus after keyboard page turns — not a control */
  outline: none;
`;

interface SafariWrapperProps {
	variant: "elements" | "split-page";
	"aria-hidden"?: boolean;
	children: ReactNode;
	$hasWidescreen?: boolean;
}

const SafariWrapper = ({
	variant,
	"aria-hidden": ariaHidden,
	children,
	$hasWidescreen,
}: SafariWrapperProps) => {
	const pageSize = useRecoilValue(PageSizeWithPaddingSelector);
	const navigationType = useRecoilValue(NavigationType);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const position = useContext(PositionContext);
	const wrapperRef = useRef<HTMLElement>(null);

	/* aria-hidden alone hides content from screen readers but its links,
	   buttons and iframes stay TABBABLE — keyboard focus walks into the
	   invisible pre-rendered page. inert removes it from the tab order too
	   (set via ref: React 18 has no first-class inert prop) */
	useEffect(() => {
		if (wrapperRef.current) wrapperRef.current.inert = !!ariaHidden;
	}, [ariaHidden]);

	const className = `${
		variant === "elements"
			? "display-elements-safari-wrapper"
			: "display-split-page-safari-wrapper"
	} ${position} ${isPreviewer ? "previewer" : ""}`;

	return (
		<StyledSafariWrapper
			ref={wrapperRef}
			tabIndex={-1}
			$height={pageSize.height}
			$width={pageSize.width}
			$navigationType={navigationType}
			id={strings.css.safariWrapper}
			className={className}
			aria-hidden={ariaHidden}
			$hasWidescreen={$hasWidescreen}
			$isInLandscape={pageSize.landscape}
			$forceSingleColumn={pageSize.forceSingleColumn}
		>
			{children}
		</StyledSafariWrapper>
	);
};

export default SafariWrapper;
